import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { REPORT_TYPE_LABELS } from '../constants/reportTypes';
import { ProposalTemplateSummary } from './TemplateLibraryView';

interface GenerateProposalModalProps {
  // Source reports and/or documents the proposal is built from. At least one
  // across both arrays is required.
  reportIds: string[];
  documentIds?: string[];
  // When provided, the template list is filtered to this workflow type. Omit
  // to show all active templates (e.g. the Proposals-tab "Create" flow).
  workflowType?: string;
  customerId: string;
  projectId?: string;
  token: string | null;
  onClose: () => void;
  // Optional "Back" affordance (used when embedded in the source-picker wizard).
  onBack?: () => void;
}

type PollStatus = 'idle' | 'submitting' | 'polling' | 'error';

const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

export const GenerateProposalModal: React.FC<GenerateProposalModalProps> = ({
  reportIds,
  documentIds,
  workflowType,
  customerId,
  projectId,
  token,
  onClose,
  onBack,
}) => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ProposalTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  // selectedId = which template the preview pane shows (focus).
  // selectedIds = the set of templates to compose into one proposal (ordered).
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [status, setStatus] = useState<PollStatus>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const workflowLabel = workflowType ? (REPORT_TYPE_LABELS[workflowType] || workflowType) : null;

  // Fetch templates. Filter by report type when a workflowType is provided;
  // otherwise list all active templates.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const url = workflowType
          ? `${apiUrl}/api/proposals/templates?reportType=${encodeURIComponent(workflowType)}`
          : `${apiUrl}/api/proposals/templates`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          if (!cancelled) setErrorMessage('Failed to load templates');
          return;
        }
        const data = await res.json();
        if (!cancelled) {
          setTemplates(data.templates || []);
          if (data.templates?.length > 0) {
            setSelectedId(data.templates[0].id);
          }
        }
      } catch {
        if (!cancelled) setErrorMessage('Failed to load templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, workflowType]);

  // Load preview when selection changes. Only PDFs (originals or pre-rendered
  // previews of docx/xlsx) can render in an iframe — loading raw docx/xlsx
  // bytes triggers a browser download instead of an inline preview.
  useEffect(() => {
    if (!selectedId || !token) return;
    const tpl = templates.find((t) => t.id === selectedId);
    const hasPdfPreview = !!tpl && (tpl.originalFileType === 'pdf' || !!tpl.previewS3Key);
    if (!hasPdfPreview) {
      setPreviewUrl(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        setPreviewLoading(true);
        setPreviewUrl(null);
        const res = await fetch(`${apiUrl}/api/proposals/templates/${selectedId}/preview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setPreviewUrl(objectUrl);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selectedId, token, templates]);

  const startGeneration = useCallback(async () => {
    if (selectedIds.length === 0 || !token || status !== 'idle') return;
    setStatus('submitting');
    setErrorMessage(null);

    try {
      const formData = new FormData();
      formData.append('templateId', selectedIds[0]);
      if (selectedIds.length > 1) {
        formData.append('templateIds', JSON.stringify(selectedIds));
      }
      formData.append('reportIds', JSON.stringify(reportIds));
      formData.append('documentIds', JSON.stringify(documentIds || []));
      formData.append('customerId', customerId);
      if (projectId) formData.append('projectId', projectId);
      if (instructions.trim()) formData.append('instructions', instructions.trim());

      const res = await fetch(`${apiUrl}/api/proposals/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start proposal generation');
      }

      const data = await res.json();
      setJobId(data.jobId);
      setStatus('polling');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to start generation');
      setStatus('error');
    }
  }, [selectedIds, token, status, reportIds, documentIds, customerId, projectId, instructions]);

  // Poll job for completion
  useEffect(() => {
    if (status !== 'polling' || !jobId) return;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${apiUrl}/api/jobs/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const job = data.job || data;

        setProgress(job.progress || 0);

        if (job.status === 'completed') {
          clearInterval(interval);
          const proposalId = job.resultPayload?.proposalId;
          if (proposalId) {
            navigate(`/proposal-editor/${proposalId}`);
            onClose();
          } else {
            setErrorMessage('Proposal completed but no ID returned');
            setStatus('error');
          }
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setErrorMessage(job.lastError || 'Proposal generation failed');
          setStatus('error');
        }
      } catch {
        // retry silently
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [status, jobId, token, navigate, onClose]);

  const selectedTemplate = templates.find((t) => t.id === selectedId) || null;
  const isWorking = status === 'submitting' || status === 'polling';

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={status === 'submitting' ? undefined : onClose}>
      <div
        className="bg-white dark:bg-dark-surface rounded-lg shadow-xl border border-gray-200 dark:border-dark-border w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {onBack && !isWorking && (
              <button
                onClick={onBack}
                title="Back to source selection"
                className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
                </svg>
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Generate Proposal</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {workflowLabel
                  ? <>Pick a template for this <span className="font-medium">{workflowLabel}</span> report.</>
                  : 'Pick one or more templates for your proposal.'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={status === 'submitting'}
            title={status === 'polling' ? 'Close — generation continues in the background' : 'Close'}
            className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>

        {errorMessage && (
          <div className="m-5 mb-0 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {errorMessage}
          </div>
        )}

        {isWorking ? (
          <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {status === 'submitting' ? 'Starting...' : 'Generating Proposal...'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              The AI is reading your report and filling the template. This can take a few minutes.
            </p>
            {progress > 0 && (
              <div className="w-full max-w-xs">
                <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-primary rounded-full h-2 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">{progress}%</p>
              </div>
            )}
            {status === 'polling' && (
              <div className="mt-6">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Continue in the background
                </button>
                <p className="text-xs text-gray-400 mt-2 max-w-xs">
                  You can close this and keep working. The proposal keeps generating and appears in your proposals (and the notification bell) when it's ready.
                </p>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : templates.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-10 text-center text-sm text-gray-500 dark:text-gray-400">
            No proposal templates available for this report type. Company admins can upload templates from company settings.
          </div>
        ) : (
          <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-5 gap-4 p-5 min-h-0">
            {/* Template list */}
            <div className="lg:col-span-2 space-y-2 overflow-y-auto">
              {templates.length > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                  Select one or more templates. Choosing multiple composes them into a single proposal — use the ↑↓ arrows to set their order.
                </p>
              )}
              {templates.map((template) => {
                const isChecked = selectedIds.includes(template.id);
                const order = selectedIds.indexOf(template.id) + 1;
                // Reorder a selected template within the composition order.
                const moveSelected = (dir: -1 | 1) => {
                  setSelectedIds((prev) => {
                    const i = prev.indexOf(template.id);
                    const j = i + dir;
                    if (i < 0 || j < 0 || j >= prev.length) return prev;
                    const next = [...prev];
                    [next[i], next[j]] = [next[j], next[i]];
                    return next;
                  });
                };
                return (
                <div
                  key={template.id}
                  onClick={() => {
                    setSelectedIds((prev) => prev.includes(template.id) ? prev.filter((x) => x !== template.id) : [...prev, template.id]);
                    setSelectedId(template.id);
                  }}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedId === template.id
                      ? 'border-primary bg-primary/5 dark:bg-primary/10'
                      : isChecked
                        ? 'border-primary/60 bg-primary/5 dark:bg-primary/10'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {}}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 text-primary border-gray-300 rounded pointer-events-none"
                    />
                    {isChecked && selectedIds.length > 1 && (
                      <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="text-xs w-5 h-5 flex items-center justify-center rounded-full bg-primary text-white font-medium">{order}</span>
                        <button
                          type="button"
                          onClick={() => moveSelected(-1)}
                          disabled={order === 1}
                          title="Move earlier in the proposal"
                          className={`text-xs w-5 h-5 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 ${
                            order === 1 ? 'opacity-30 cursor-default' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSelected(1)}
                          disabled={order === selectedIds.length}
                          title="Move later in the proposal"
                          className={`text-xs w-5 h-5 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 ${
                            order === selectedIds.length ? 'opacity-30 cursor-default' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          ↓
                        </button>
                      </span>
                    )}
                    <h3 className="font-medium text-gray-900 dark:text-white">{template.title}</h3>
                    {template.isSystemDefault && (
                      <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full">
                        Default
                      </span>
                    )}
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded uppercase">
                      {template.originalFileType}
                    </span>
                  </div>
                  {template.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{template.description}</p>
                  )}
                </div>
                );
              })}

              <div className="pt-2">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Additional instructions (optional)
                </label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  rows={3}
                  placeholder="e.g., Focus on the commercial property section, use the proposed carrier..."
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="lg:col-span-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-hidden">
              {!selectedTemplate ? (
                <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 p-8">
                  Select a template to preview.
                </div>
              ) : previewLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              ) : previewUrl ? (
                <iframe src={previewUrl} title={selectedTemplate.title} className="w-full h-full min-h-[50vh]" />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 p-8">
                  Preview not available yet.
                </div>
              )}
            </div>
          </div>
        )}

        {!isWorking && templates.length > 0 && (
          <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={startGeneration}
              disabled={selectedIds.length === 0 || status !== 'idle'}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              {selectedIds.length > 1 ? `Generate Proposal (${selectedIds.length} templates)` : 'Generate Proposal'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateProposalModal;
